import React, { useState, useRef, useEffect } from "react";
import Button from "../Button/Button";
import PlayIcon from "../../assets/svg/play-icon";
import PauseIcon from "../../assets/svg/pause-icon";
import DownloadTwoIcon from "../../assets/svg/download-two-icon";
import VerbafloLogoIcon from "../../assets/images/verbaflo-logo.png";
import UserIcon from "../../assets/svg/user-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import { Avatar } from "../Avatar/Avatar";
import { getDateTimeByFormat } from "../../redux/slices/dateTimeFormatSlice";
import OutboundCallIcon from "../../assets/svg/outbound-call-icon";
import InboundCallIcon from "../../assets/svg/inbound-call-icon";
import LineArrowRightIcon from "../../assets/svg/line-arrow-right-icon";
import { Tooltip } from "../Tooltip/Tooltip";
import VoiceMailIcon from "../../assets/svg/voice-mail-icon";

type Modality = "email" | "call" | "messenger" | "chat";

interface VoiceCallPlayerProps {
  audioSrc?: string;
  duration?: string;
  showDownloadButton?: boolean;
  onDownload?: () => void;
  isSent?: boolean;
  playerContainerClassName?: string;
  showWaveform?: boolean;
  waveformBars?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  iconType?: "verbaflo" | "participant" | "agent";
  participantInfo?: {
    text: string;
    size?: number;
    showOnlineStatus?: boolean;
    isOnline?: boolean;
    fullName?: string;
  };
  modality?: Modality;
  showModality?: boolean;
  showTimestamp?: boolean;
  timestamp?: string;
  campaignName?: string;
  isVoiceMail?: boolean;
  campaignId?: string;
}

const VoiceCallPlayer: React.FC<VoiceCallPlayerProps> = ({
  audioSrc = "/path/to/audio.mp3",
  duration = "3:45s",
  showDownloadButton = true,
  onDownload,
  isSent = false,
  playerContainerClassName,
  showWaveform = true,
  waveformBars = 20,
  onPlay,
  onPause,
  onEnded,
  iconType,
  participantInfo,
  modality = "call",
  showModality = true,
  showTimestamp = true,
  timestamp,
  campaignName,
  isVoiceMail = false,
  campaignId,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);

  const modalityConfig = {
    email: {
      icon: VerbamailIcon,
      label: "VerbaMail",
      color: "text-verba-mail dark:text-verba-mail-dk",
    },
    call: {
      icon: VerbacallIcon,
      label: "VerbaCall",
      color: "text-verba-lavender dark:text-verba-lavender-dk",
    },
    messenger: {
      icon: VerbamessengerIcon,
      label: "VerbaMessenger",
      color: "text-verba-messenger dark:text-verba-messenger-dk",
    },
    chat: {
      icon: VerbachatIcon,
      label: "VerbaChat",
      color: "text-verba-chat dark:text-verba-chat-dk",
    },
  };

  const { icon: ModalityIcon, label, color } = modalityConfig[modality];

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setAudioDuration(audio.duration || 0);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEnded?.();
    };

    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
    };
  }, [audioSrc, onEnded]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        onPause?.();
      } else {
        document
          ?.querySelectorAll("audio")
          ?.forEach((a) => a !== audioRef.current && a?.pause());
        audioRef.current.play();
        onPlay?.();
      }
    }
    setIsPlaying(!isPlaying);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    } else {
      const link = document.createElement("a");
      link.href = audioSrc;
      link.download = "audio.mp3";
      link.target = "_blank";
      link.click();
    }
  };

  // Add click-to-seek functionality on waveform
  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !waveformRef.current || audioDuration === 0)
      return;

    const { left, width } = waveformRef.current.getBoundingClientRect();
    const clickX = e.clientX - left;
    const clickRatio = clickX / width;
    const newTime = clickRatio * audioDuration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const renderIcon = () => {
    if (!iconType) return null;

    return (
      <>
        {iconType === "verbaflo" && (
          <div>
            <img
              src={VerbafloLogoIcon}
              alt="Verbaflo Logo"
              className="w-[24px] h-[24px]"
            />
          </div>
        )}
        {iconType === "participant" && participantInfo && (
          <div
            className={`flex items-center gap-2 ${
              isSent ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <div className="shrink-0 relative">
              <Avatar
                size={participantInfo.size || 24}
                text={participantInfo.text}
                className="shrink-0"
              />
              {participantInfo.showOnlineStatus && (
                <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
                  <div
                    className={`w-[4px] h-[4px] ${
                      participantInfo.isOnline
                        ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                        : "bg-offline dark:bg-offline-dk"
                    } rounded-full`}
                  ></div>
                </div>
              )}
            </div>
            {participantInfo.fullName && (
              <span className="text-sm truncate max-w-28 font-medium text-heading-text dark:text-heading-text-dk">
                {participantInfo.fullName}
              </span>
            )}
          </div>
        )}
        {iconType === "agent" && (
          <div>
            <UserIcon className="w-[9px] h-[13px] text-verba-indigo dark:text-verba-indigo-dk" />
          </div>
        )}
      </>
    );
  };

  const renderWaveform = () => {
    if (!showWaveform) return null;

    const pattern = [
      16, 10, 16, 6, 16, 10, 16, 4, 16, 12, 16, 6, 16, 8, 16, 10, 16, 6, 16, 8,
      16, 8, 16, 6, 16, 10, 16, 4, 16, 12, 16, 6, 16, 8, 16, 10, 16, 6, 16, 8,
      16, 8, 16, 6, 16, 10, 16, 4, 16, 12,
    ];

    const progressPercentage =
      audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

    return (
      <div
        ref={waveformRef}
        className="flex gap-[4px] items-center justify-center flex-1 cursor-pointer"
        onClick={handleWaveformClick}
      >
        {[...Array(waveformBars)].map((_, i) => {
          const height = pattern[i % pattern.length] || 8;
          const barPosition = ((i + 1) / waveformBars) * 100;
          const isPlayed = barPosition <= progressPercentage;

          return (
            <div
              key={i}
              className={`w-[2.5px] rounded-full transition-all duration-300 ease-in-out ${
                isPlayed
                  ? isPlaying
                    ? `animate-wave animate-delay-${
                        i % 5
                      } bg-pure-white dark:bg-pure-white-dk`
                    : "bg-pure-white dark:bg-pure-white-dk"
                  : isPlaying
                  ? `animate-wave animate-delay-${
                      i % 5
                    } bg-pure-white/40 dark:bg-pure-white-dk/40`
                  : "bg-heading-text/60 dark:bg-heading-text-dk/60"
              }`}
              style={{
                height: `${height}px`,
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={`w-fit  flex flex-col gap-[4px] ${
        isSent ? "ml-auto" : "mr-auto"
      }`}
    >
      <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
        {isSent ? (
          <div className="flex gap-1 items-center">
            <OutboundCallIcon className="w-[14px] h-[15px] text-verba-green-success dark:text-verba-green-success-dk" />
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              Outbound Call
            </span>
          </div>
        ) : (
          <div className="flex gap-1 items-center">
            <InboundCallIcon className="w-[14px] h-[15px] text-verba-blue dark:text-verba-blue-dk" />
            <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
              Inbound Call
            </span>
          </div>
        )}
      </span>
      <div
        className={`${
          isVoiceMail
            ? "bg-gradient-ai-card flex flex-col gap-[4px] rounded-[8px] px-3 py-2"
            : ""
        } `}
      >
        {isVoiceMail && (
          <div className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-[2px]">
              <VoiceMailIcon className="w-[15px] h-[15px] " />
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                VoiceMail
              </span>
            </div>
            <div className="flex items-center gap-[2px]">
              <img
                src={VerbafloLogoIcon}
                alt="Verbaflo Logo"
                className="w-[20px] h-[20px]"
              />
              <p className="text-verba-indigo dark:text-verba-indigo-dk text-xs font-bold">
                VerbaFlo.
              </p>
            </div>
          </div>
        )}
        <div
          className={`${
            isPlaying
              ? "bg-verba-blue dark:bg-verba-blue-dk border-verba-blue dark:border-verba-blue"
              : "bg-verba-blue/5 dark:bg-verba-blue-dk/5 border border-verba-blue/20 dark:border-verba-blue-dk/20"
          } p-[8px] rounded-t-[8px]   ${isSent ? "rounded-bl-[8px]" : "rounded-br-[8px]"} flex items-center gap-[10px] justify-between ${playerContainerClassName}`}
        >
          <audio ref={audioRef} src={audioSrc} />

          <Button
            buttonContainerClass={`w-[32px] h-[32px] ${
              isPlaying
                ? "bg-fixed-white dark:bg-fixed-white-dk"
                : "bg-verba-blue dark:bg-verba-blue-dk"
            } rounded-full`}
            Icon={isPlaying ? PauseIcon : PlayIcon}
            iconClass={`w-[18px] h-[18px] ${
              isPlaying
                ? "text-verba-blue dark:text-verba-blue-dk"
                : "text-fixed-white dark:text-fixed-white-dk"
            }`}
            onClick={handlePlayPause}
          />

          {renderWaveform()}

          <div className="flex items-center gap-[8px]">
            <p
              className={`text-md font-normal ${
                isPlaying
                  ? "text-pure-white dark:text-pure-white-dk"
                  : "text-data dark:text-data-dk"
              }`}
            >
              {duration}
            </p>
            {showDownloadButton && (
              <Button
                buttonContainerClass="p-"
                Icon={DownloadTwoIcon}
                iconClass={`w-[18px] h-[18px] ${
                  isPlaying
                    ? "text-pure-white dark:text-pure-white-dk"
                    : "text-data dark:text-data-dk"
                }`}
                onClick={handleDownload}
              />
            )}
          </div>
        </div>
      </div>
      {isSent && (
       <div className="flex items-center group bg-hover-effect dark:bg-hover-effect-dk hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-heading-text dark:text-heading-text-dk hover:text-pure-white dark:hover:text-pure-white  rounded-b-[8px] px-1.5 py-1 w-[240px] ml-auto mt-[-4px]">
        <div className="flex items-center gap-1.5 cursor-pointer justify-center max-w-[230px]">
          <LineArrowRightIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk group-hover:text-pure-white dark:group-hover:text-pure-white" />
          <span className="text-md font-medium">Workflo</span>
          <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk group-hover:bg-pure-white dark:group-hover:bg-pure-white rounded-full"></div>
          {campaignName && (
            <Tooltip content={campaignName}>
            <span 
              className="text-md font-medium truncate max-w-[63%] hover:underline cursor-pointer" 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                window.location.href = `/dashboard/campaigns/${campaignId}/preview`;
              }}
            >
              {campaignName}
            </span>
            </Tooltip>
          )}
        </div>
      </div>
      )}
      <div
        className={`flex items-center justify-end gap-[4px] ${
          isSent ? "flex-row" : "flex-row-reverse"
        }`}
      >
        {/* {isSent && (
          <Tooltip content={campaignName || ""}>
            <div className="flex items-center gap-[2px] cursor-pointer">
              <LineArrowRightIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
            </div>
          </Tooltip>
        )} */}
        {showModality && (
          <>
            <div className="flex items-center gap-[2px]">
              <ModalityIcon className={`w-[14px] h-[14px] ${color}`} />
              {isSent && (
                <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                  {label}
                </p>
              )}
            </div>
          </>
        )}

        {showTimestamp && timestamp && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
                {getDateTimeByFormat(new Date(timestamp || "")).time}
              </p>
            </div>
          </>
        )}

        {iconType && (
          <>
            <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
            {renderIcon()}
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceCallPlayer;
